/**
 * A market: the country a group trades in, closed and named in code. Every
 * fact that follows from where a group trades is a column of its row here,
 * and nowhere else: the phone country and its mobile rule, the clock, the
 * currency, the locale pages format in, the examples a phone field shows.
 *
 * Organisation owns the choice (`set market`); this table owns what a choice
 * means. It imports nothing and uses no Date or Intl (E01, R20): data plus
 * lookups, so a page, a values module and a decider read the same row.
 *
 * Adding a market is one row below (its examples are its words). The union is
 * the payload type (`market set`), so T02 holds: a literal union, not free text.
 */
export const MARKET_CODES = ['MY', 'SG'] as const;
export type Market = (typeof MARKET_CODES)[number];

export interface MarketFacts {
  readonly market: Market;
  /** What staff read in a picker: "Malaysia". */
  readonly name: string;
  readonly phone: {
    /** libphonenumber's country: how a number typed without `+` is read, and which numbers are "ours" for the mobile rule and national display. */
    readonly country: Market;
    /** A national number of ours is a mobile when it starts with one of these (MY 01x reads '1'; SG 8xxx and 9xxx). Numbers of other countries pass (ruling 2026-10-14). */
    readonly mobilePrefixes: readonly string[];
    /** The national example a phone field shows: "012-345 6789". */
    readonly example: string;
    /** A number from a neighbouring market, written with its country code: what "from abroad" shows. */
    readonly abroadExample: string;
  };
  /** Fixed offset, no daylight saving (instant.ts does integer calendar math on it; no tz database). */
  readonly utcOffsetMinutes: number;
  /** IANA zone: stamped on businessDay.timezone; what pages pass to Intl. */
  readonly timezone: string;
  /**
   * Every amount in a payload is an integer of minor units (fields named `sen`,
   * `capSen`, `budgetSen`, ...: the name is historical, the unit is "minor unit
   * of the group's currency"). The currency is the group's, never per amount,
   * which is why a group's market is set once (organisation `set market`).
   */
  readonly currency: { readonly code: string; readonly symbol: string; readonly minorUnits: 2 };
  /** BCP 47 tag for Intl number and date formatting on pages. */
  readonly locale: string;
}

export const MARKETS = {
  MY: {
    market: 'MY',
    name: 'Malaysia',
    phone: { country: 'MY', mobilePrefixes: ['1'], example: '012-345 6789', abroadExample: '+65 9123 4567' },
    utcOffsetMinutes: 480,
    timezone: 'Asia/Kuala_Lumpur',
    currency: { code: 'MYR', symbol: 'RM', minorUnits: 2 },
    locale: 'en-MY',
  },
  SG: {
    market: 'SG',
    name: 'Singapore',
    phone: { country: 'SG', mobilePrefixes: ['8', '9'], example: '9123 4567', abroadExample: '+60 12-345 6789' },
    utcOffsetMinutes: 480,
    timezone: 'Asia/Singapore',
    currency: { code: 'SGD', symbol: 'S$', minorUnits: 2 },
    locale: 'en-SG',
  },
} as const satisfies { readonly [M in Market]: MarketFacts & { readonly market: M } };

/** What a group trades in before it sets a market: every group that existed before markets did. */
export const DEFAULT_MARKET: Market = 'MY';

/**
 * The one place "no market set reads as Malaysia" lives. The decider passes
 * its loaded row's code (undefined when the group never set one), a page its
 * shown row's, a projection the code it kept.
 */
export function marketOf(code: Market | undefined): MarketFacts {
  return MARKETS[code ?? DEFAULT_MARKET];
}

/**
 * The market of an outlet: the one it named (organisation `set outlet`), else
 * its group's (`code`, read as marketOf reads it). What is decided at an outlet
 * (its business day, a redemption, a draw, an identification) reads this; what
 * is decided for the whole group reads marketOf.
 */
export function marketAt(outlet: { readonly market?: Market | undefined } | undefined, code: Market | undefined): MarketFacts {
  return marketOf(outlet?.market ?? code);
}

/**
 * The market whose zone a businessDay names: how a projection that sees only
 * an outlet-scoped event recovers the clock it was stamped in. A zone no row
 * names (none today) reads as the default. Derived from MARKETS, never listed twice.
 */
export function marketOfTimezone(timezone: string): MarketFacts {
  return Object.values<MarketFacts>(MARKETS).find((m) => m.timezone === timezone) ?? marketOf(undefined);
}

/**
 * The westmost offset of any market: a calendar date that has ended there has
 * ended in every market. The lapse schedule, which knows no group, reads
 * "yesterday" here, so it never names a date still running somewhere.
 */
export const WESTMOST_UTC_OFFSET_MINUTES: number = Math.min(...Object.values<MarketFacts>(MARKETS).map((m) => m.utcOffsetMinutes));
